import type { Frame } from "../renderer/Frame";
import type { DPoint, DrawingObject } from "./types";

export interface XY { x: number; y: number }

export const toXY = (f: Frame, p: DPoint): XY => ({ x: f.xt(p.time), y: f.y(p.price) });
export const screenPts = (f: Frame, d: DrawingObject): XY[] => d.points.map((p) => toXY(f, p));

export function distToSegment(p: XY, a: XY, b: XY): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function distToLine(p: XY, a: XY, b: XY): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  return len ? Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x) / len : Math.hypot(p.x - a.x, p.y - a.y);
}

export function inRect(p: XY, a: XY, b: XY, pad = 0): boolean {
  return p.x >= Math.min(a.x, b.x) - pad && p.x <= Math.max(a.x, b.x) + pad && p.y >= Math.min(a.y, b.y) - pad && p.y <= Math.max(a.y, b.y) + pad;
}

export function inPolygon(p: XY, poly: XY[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

// Ray from a through b extended to the plot edge.
export function rayEnd(f: Frame, a: XY, b: XY): XY {
  const dx = b.x - a.x, dy = b.y - a.y;
  if (!dx && !dy) return b;
  const k = 4000 / (Math.hypot(dx, dy) || 1);
  return { x: a.x + dx * k, y: a.y + dy * k };
}

// Parallel channel: offset vector so the second line passes through the 3rd point.
export function channelOffset(a: XY, b: XY, c: XY): XY {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy || 1;
  const t = ((c.x - a.x) * dx + (c.y - a.y) * dy) / len2;
  return { x: c.x - (a.x + t * dx), y: c.y - (a.y + t * dy) };
}

export interface TextBox { x: number; y: number; w: number; h: number }

export function textBox(ctx: CanvasRenderingContext2D, at: XY, text: string, fontSize: number, pad = 6): TextBox {
  ctx.font = `${fontSize}px 'JetBrains Mono', monospace`;
  const lines = text.split("\n");
  const w = Math.max(...lines.map((l) => ctx.measureText(l).width)) + pad * 2;
  const h = lines.length * (fontSize + 4) + pad * 2 - 4;
  return { x: at.x, y: at.y - h / 2, w, h };
}

export const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
export const FIB_EXT_LEVELS = [0, 0.618, 1, 1.272, 1.618, 2, 2.618];
